import {
    IconBrandGithub,
    IconBrandLinkedin,
    IconLink,
    IconMail,
    IconMapPin,
    IconPhone,
    IconWorld,
} from '@tabler/icons-react';
import { forwardRef, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { getContactHref } from "../engines/atsCvContactIcons.js";
import {
    getProjectLinkMeta,
    normalizeProjectUrl,
    openProjectLink,
} from "../engines/atsCvProjectLink.js";
import { buildAtsCvData } from "../engines/atsCvFormEngine.js";
import {
    ATS_CV_PAGE_CONTENT_HEIGHT,
    buildAtsCvPreviewBlockMeta,
    measurePreviewBlockHeight,
    packBlocksIntoPages,
} from "../engines/atsCvPreviewBlocks.js";
import {
    ATS_CV_MARGIN_BOTTOM,
    ATS_CV_MARGIN_TOP,
    ATS_CV_PAGE_MIN_HEIGHT,
    ATS_CV_PAGE_PADDING,
    ATS_CV_PAGE_WIDTH,
    ATS_CV_RAIL_PAD_LEFT,
} from "../engines/atsCvLayout.js";
import "@/styles/ats-cv-preview.scss";

export { ATS_CV_PAGE_WIDTH } from "../engines/atsCvLayout.js";

const SERIF = 'Georgia, "Times New Roman", Times, serif';

const PAGE_SHEET_STYLE = {
    width: ATS_CV_PAGE_WIDTH,
    height: ATS_CV_PAGE_MIN_HEIGHT,
    background: "#fff",
    color: "#111",
    padding: ATS_CV_PAGE_PADDING,
    boxSizing: "border-box",
    fontFamily: SERIF,
    fontSize: 11,
    lineHeight: 1.5,
    overflow: "hidden",
    flexShrink: 0,
};

const SANS = '"Helvetica Neue", Helvetica, Arial, sans-serif';

/** The sidebar template sets its type in sans-serif and keeps the rail clear of the text. */
const railSheetStyle = (template) => ({
    ...PAGE_SHEET_STYLE,
    padding: `${ATS_CV_MARGIN_TOP}px 36px ${ATS_CV_MARGIN_BOTTOM}px ${ATS_CV_RAIL_PAD_LEFT[template] ?? ATS_CV_RAIL_PAD_LEFT.sidebar}px`,
    fontFamily: SANS,
    color: "#333",
});

/* One object per template, built once: the sheet style is a dependency of the
   pagination effect, so a fresh object each render would re-run it forever. */
const RAIL_SHEET_STYLES = Object.fromEntries(
    Object.keys(ATS_CV_RAIL_PAD_LEFT).map((key) => [key, railSheetStyle(key)]),
);

/** The banner template: sans-serif body under serif headings, on a plain white page. */
const BANNER_SHEET_STYLE = {
    ...PAGE_SHEET_STYLE,
    fontFamily: SANS,
    color: "#333",
};

/** The rail is the same on every page, so it is a real element the PDF export can paint. */
function SidebarBackground() {
    return <div className="ats-cv-sidebar-bg" aria-hidden />;
}

function SectionTitleBar({ title, rule = false, split = false, pill = false }) {
    if (pill) {
        return (
            <section className="ats-cv-section ats-cv-section--title-only ats-cv-section--pill">
                <h2 className="ats-cv-section__title ats-cv-section__title--pill">{title}</h2>
            </section>
        );
    }
    return (
        <section className="ats-cv-section ats-cv-section--title-only">
            <h2 className={`ats-cv-section__title${rule ? " ats-cv-section__title--rule" : ""}`}>
                {split ? <span className="ats-cv-section__lead" aria-hidden /> : null}
                {rule ? <span>{title}</span> : title}
                {rule && !split ? <span className="ats-cv-section__rule" aria-hidden /> : null}
            </h2>
        </section>
    );
}

function CvSection({ title, children }) {
    if (!children) return null;
    return (
        <section className="ats-cv-section">
            <h2 className="ats-cv-section__title">{title}</h2>
            <div className="ats-cv-section__body">{children}</div>
        </section>
    );
}

function SplitEntryHeader({ leftTitle, leftSubtitle, rightTitle, rightSubtitle }) {
    return (
        <div className="ats-cv-split-row">
            <div className="ats-cv-split-row__left">
                {leftTitle ? <p className="ats-cv-split-row__title">{leftTitle}</p> : null}
                {leftSubtitle ? (
                    <p className="ats-cv-split-row__subtitle">{leftSubtitle}</p>
                ) : null}
            </div>
            {(rightTitle || rightSubtitle) && (
                <div className="ats-cv-split-row__right">
                    {rightTitle ? <p className="ats-cv-split-row__title">{rightTitle}</p> : null}
                    {rightSubtitle ? (
                        <p className="ats-cv-split-row__subtitle">{rightSubtitle}</p>
                    ) : null}
                </div>
            )}
        </div>
    );
}

function BulletList({ lines, className = "" }) {
    if (!lines?.length) return null;
    return (
        <ul className={`ats-cv-list ${className}`.trim()}>
            {lines.map((line, i) => (
                <li key={`${i}-${line}`} className="ats-cv-list__item">
                    {line}
                </li>
            ))}
        </ul>
    );
}

function TwoColumnBullets({ items }) {
    if (!items?.length) return null;
    if (items.length === 1) {
        return (
            <div className="ats-cv-list-block">
                <BulletList lines={items} />
            </div>
        );
    }
    const mid = Math.ceil(items.length / 2);
    const left = items.slice(0, mid);
    const right = items.slice(mid);
    return (
        <div className="ats-cv-two-col">
            <div className="ats-cv-two-col__col">
                <BulletList lines={left} />
            </div>
            <div className="ats-cv-two-col__col">
                <BulletList lines={right.length > 0 ? right : []} />
            </div>
        </div>
    );
}

function ContactIcon({ icon: Icon }) {
    return <Icon className="ats-cv-contact-row__icon" size={10} stroke={1.8} aria-hidden />;
}

/** @param {{ type: string, icon: import("react").ComponentType, text: string }} item */
function ContactRowItem({ item, label = "" }) {
    const href = getContactHref(item.type, item.text);
    const isExternal = item.type === "linkedin" || item.type === "website";

    // A label ("Email :") stands in for the icon in the split template.
    const inner = (
        <>
            {label ? null : <ContactIcon icon={item.icon} />}
            <span>{item.text}</span>
        </>
    );
    const labelNode = label ? <span className="ats-cv-sidebar__label">{label}</span> : null;

    if (!href) {
        return (
            <li className="ats-cv-contact-row__item" data-contact-type={item.type}>
                {labelNode}
                {inner}
            </li>
        );
    }

    return (
        <li className="ats-cv-contact-row__item" data-contact-type={item.type}>
            {labelNode}
            <a
                href={href}
                className="ats-cv-contact-row__link"
                title={href}
                target={isExternal ? "_blank" : undefined}
                rel={isExternal ? "noopener noreferrer" : undefined}
                onClick={(e) => {
                    if (isExternal) {
                        e.preventDefault();
                        openProjectLink(href);
                    }
                }}
            >
                {inner}
            </a>
        </li>
    );
}

function CvHeader({ d }) {
    const contacts = [
        d.location
            ? { type: "location", icon: IconMapPin, text: d.location }
            : null,
        d.email ? { type: "email", icon: IconMail, text: d.email } : null,
        d.phone ? { type: "phone", icon: IconPhone, text: d.phone } : null,
        d.linkedin ? { type: "linkedin", icon: IconBrandLinkedin, text: d.linkedin } : null,
        d.website
            ? {
                  type: "website",
                  icon: d.website.toLowerCase().includes("github.com")
                      ? IconBrandGithub
                      : IconWorld,
                  text: d.website,
              }
            : null,
    ].filter(Boolean);

    // With a photo the header becomes a row — picture on the left, the name
    // and contacts beside it — because a centred name under a portrait reads
    // as a caption for it rather than as the heading of the page.
    if (d.photo) {
        return (
            <header className="ats-cv-header ats-cv-header--with-photo">
                {/* The box is the shape the crop dialog showed. Its width is
                    fixed by the layout; only its height follows the choice, so
                    the text column never moves when the shape changes. */}
                <img
                    className="ats-cv-header__photo"
                    src={d.photo}
                    alt=""
                    style={{ height: `${Math.round(86 / (d.photoAspect || 1))}px` }}
                />
                <div className="ats-cv-header__body">
                    <h1 className="ats-cv-header__name">{d.fullName || "Your Name"}</h1>
                    {d.jobTitle ? <p className="ats-cv-header__title">{d.jobTitle}</p> : null}
                    {contacts.length > 0 && (
                        <ul className="ats-cv-contact-row">
                            {contacts.map((c) => (
                                <ContactRowItem key={`${c.type}-${c.text}`} item={c} />
                            ))}
                        </ul>
                    )}
                </div>
            </header>
        );
    }

    return (
        <header className="ats-cv-header">
            <h1 className="ats-cv-header__name">{d.fullName || "Your Name"}</h1>
            {d.jobTitle ? <p className="ats-cv-header__title">{d.jobTitle}</p> : null}
            {contacts.length > 0 && (
                <ul className="ats-cv-contact-row">
                    {contacts.map((c) => (
                        <ContactRowItem key={`${c.type}-${c.text}`} item={c} />
                    ))}
                </ul>
            )}
        </header>
    );
}

/** Bold header: a coloured band with the photo, the name, and the role in a white tag. */
function BoldHeader({ d }) {
    return (
        <header className="ats-cv-header ats-cv-header--bold">
            {d.photo ? (
                <ShapedPhoto src={d.photo} size={92} radius={0} ring={0} className="ats-cv-bold__photo" />
            ) : null}
            <div className="ats-cv-bold__who">
                <h1 className="ats-cv-header__name">{d.fullName || "Your Name"}</h1>
                {d.jobTitle ? <span className="ats-cv-bold__tag">{d.jobTitle}</span> : null}
            </div>
        </header>
    );
}

/** Role in capitals on the left, "company / dates" on the right, the detail beneath. */
function BoldEntry({ title, meta, sub, children }) {
    return (
        <div className="ats-cv-entry ats-cv-bd">
            <div className="ats-cv-bd__top">
                <span className="ats-cv-bd__title">{title}</span>
                {meta ? <span className="ats-cv-bd__meta">{meta}</span> : null}
            </div>
            {sub ? <p className="ats-cv-bd__sub">{sub}</p> : null}
            {children}
        </div>
    );
}

/** Name and role only: in the sidebar template the contacts and photo live in the rail. */
function SidebarMainHeader({ d, split = false }) {
    return (
        <header className="ats-cv-header ats-cv-header--sidebar">
            <h1 className="ats-cv-header__name">{d.fullName || "Your Name"}</h1>
            {split ? (
                <div className="ats-cv-header__titlerow">
                    {d.jobTitle ? <p className="ats-cv-header__title">{d.jobTitle}</p> : null}
                    <span className="ats-cv-section__rule" aria-hidden />
                </div>
            ) : d.jobTitle ? (
                <p className="ats-cv-header__title">{d.jobTitle}</p>
            ) : null}
        </header>
    );
}

/** Title and date share a heavy rule; the company sits under a lighter one. */
function RuledEntry({ title, date, subtitle, children }) {
    return (
        <div className="ats-cv-entry ats-cv-ru">
            <div className="ats-cv-ru__top">
                <span className="ats-cv-ru__title">{title}</span>
                {date ? <span className="ats-cv-ru__date">{date}</span> : null}
            </div>
            {subtitle ? <p className="ats-cv-ru__sub">{subtitle}</p> : null}
            {children}
        </div>
    );
}

/** Date column on the left, a rule with a marker, then what the entry is. */
function TimelineEntry({ lead, leadSub, heading, subheading, children }) {
    return (
        <div className="ats-cv-entry ats-cv-tl">
            <div className="ats-cv-tl__lead">
                {lead ? <p className="ats-cv-tl__lead-main">{lead}</p> : null}
                {leadSub ? <p className="ats-cv-tl__lead-sub">{leadSub}</p> : null}
            </div>
            <div className="ats-cv-tl__body">
                <span className="ats-cv-tl__dot" aria-hidden />
                {heading ? <div className="ats-cv-tl__heading">{heading}</div> : null}
                {subheading ? <p className="ats-cv-tl__sub">{subheading}</p> : null}
                {children}
            </div>
        </div>
    );
}

/**
 * The photo as a shaped picture of its own: a circle, or a rounded square when
 * `radius` is below 0.5 (a fraction of the size).
 *
 * A CSS `border-radius` does not survive the PDF export, which places images as
 * rectangles, so the shape (and its frame) is cut into the pixels instead.
 */
function ShapedPhoto({ src, size = 104, radius = 0.5, ring = 3, className = "ats-cv-sidebar__photo" }) {
    const [shaped, setShaped] = useState("");

    useEffect(() => {
        let cancelled = false;
        const img = new Image();
        img.onload = () => {
            if (cancelled) return;
            const scale = 4;
            const px = size * scale;
            const canvas = document.createElement("canvas");
            canvas.width = px;
            canvas.height = px;
            const ctx = canvas.getContext("2d");
            const frame = ring * scale;
            const outer = px * radius;
            const inner = Math.max(0, outer - frame);

            const shape = (x, y, w, h, r) => {
                ctx.beginPath();
                ctx.moveTo(x + r, y);
                ctx.arcTo(x + w, y, x + w, y + h, r);
                ctx.arcTo(x + w, y + h, x, y + h, r);
                ctx.arcTo(x, y + h, x, y, r);
                ctx.arcTo(x, y, x + w, y, r);
                ctx.closePath();
            };

            ctx.fillStyle = "#ffffff";
            shape(0, 0, px, px, outer);
            ctx.fill();

            ctx.save();
            shape(frame, frame, px - frame * 2, px - frame * 2, inner);
            ctx.clip();
            const side = Math.min(img.naturalWidth, img.naturalHeight);
            ctx.drawImage(
                img,
                (img.naturalWidth - side) / 2,
                (img.naturalHeight - side) / 2,
                side,
                side,
                frame,
                frame,
                px - frame * 2,
                px - frame * 2,
            );
            ctx.restore();
            setShaped(canvas.toDataURL("image/png"));
        };
        img.onerror = () => { if (!cancelled) setShaped(""); };
        img.src = src;
        return () => { cancelled = true; };
    }, [src, size, radius, ring]);

    if (!shaped) return <div className={`${className}-gap`} style={{ width: size, height: size, flex: "none" }} />;
    return <img className={className} src={shaped} alt="" style={{ width: size, height: size }} />;
}

function CircularPhoto({ src, size = 104 }) {
    return <ShapedPhoto src={src} size={size} radius={0.5} ring={3} />;
}

/** Banner header: name and role, the framed photo, then the contacts — all on the coloured band. */
function BannerHeader({ d }) {
    const contacts = [
        d.phone ? { type: "phone", icon: IconPhone, text: d.phone } : null,
        d.email ? { type: "email", icon: IconMail, text: d.email } : null,
        d.location ? { type: "location", icon: IconMapPin, text: d.location } : null,
        d.linkedin ? { type: "linkedin", icon: IconBrandLinkedin, text: d.linkedin } : null,
        d.website
            ? {
                  type: "website",
                  icon: d.website.toLowerCase().includes("github.com") ? IconBrandGithub : IconWorld,
                  text: d.website,
              }
            : null,
    ].filter(Boolean);

    return (
        <header className="ats-cv-header ats-cv-header--banner">
            <div className="ats-cv-banner__who">
                <h1 className="ats-cv-header__name">{d.fullName || "Your Name"}</h1>
                {d.jobTitle ? <p className="ats-cv-header__title">{d.jobTitle}</p> : null}
            </div>
            {d.photo ? (
                <ShapedPhoto src={d.photo} size={92} radius={0.16} ring={4} className="ats-cv-banner__photo" />
            ) : null}
            {contacts.length > 0 ? (
                <ul className="ats-cv-contact-row ats-cv-banner__contacts">
                    {contacts.map((c) => (
                        <ContactRowItem key={`${c.type}-${c.text}`} item={c} />
                    ))}
                </ul>
            ) : null}
        </header>
    );
}

/** Labels the split template prints in place of the contact icons. */
const CONTACT_LABELS = {
    location: "Address :",
    phone: "Phone :",
    email: "Email :",
    linkedin: "LinkedIn :",
    website: "Website :",
};

function SidebarSection({ title, children }) {
    return (
        <section className="ats-cv-sidebar__section">
            <h2 className="ats-cv-sidebar__title">{title}</h2>
            {children}
        </section>
    );
}

function SidebarList({ items }) {
    return (
        <ul className="ats-cv-sidebar__list">
            {items.map((item, i) => (
                <li key={`${i}-${item}`}>{item}</li>
            ))}
        </ul>
    );
}

/** Photo, about, contacts, skills, languages, certifications — page one's left rail. */
function SidebarContent({ d, t, split = false, bold = false }) {
    const contacts = [
        d.location ? { type: "location", icon: IconMapPin, text: d.location } : null,
        d.email ? { type: "email", icon: IconMail, text: d.email } : null,
        d.phone ? { type: "phone", icon: IconPhone, text: d.phone } : null,
        d.linkedin ? { type: "linkedin", icon: IconBrandLinkedin, text: d.linkedin } : null,
        d.website
            ? {
                  type: "website",
                  icon: d.website.toLowerCase().includes("github.com") ? IconBrandGithub : IconWorld,
                  text: d.website,
              }
            : null,
    ].filter(Boolean);

    return (
        <aside className={`ats-cv-sidebar${bold ? " ats-cv-sidebar--bold" : ""}`}>
            {d.photo && !bold ? <CircularPhoto src={d.photo} /> : null}
            {d.summary ? (
                <SidebarSection title={t.summary}>
                    <p className="ats-cv-sidebar__text">{d.summary}</p>
                </SidebarSection>
            ) : null}
            {contacts.length > 0 ? (
                <SidebarSection title="Contact">
                    <ul className={`ats-cv-sidebar__contacts${split ? " ats-cv-sidebar__contacts--labelled" : ""}`}>
                        {contacts.map((c) => (
                            <ContactRowItem
                                key={`${c.type}-${c.text}`}
                                item={c}
                                label={split ? CONTACT_LABELS[c.type] : ""}
                            />
                        ))}
                    </ul>
                </SidebarSection>
            ) : null}
            {d.skills.length > 0 ? (
                <SidebarSection title={t.skills}>
                    <SidebarList items={d.skills} />
                </SidebarSection>
            ) : null}
            {d.languages.length > 0 ? (
                <SidebarSection title={t.languages}>
                    <SidebarList items={d.languages} />
                </SidebarSection>
            ) : null}
            {d.certifications.length > 0 ? (
                <SidebarSection title={t.certifications}>
                    <SidebarList items={d.certifications} />
                </SidebarSection>
            ) : null}
        </aside>
    );
}

function ExperienceBlock({ exp, sidebar, split, bold }) {
    if (sidebar && bold) {
        return (
            <BoldEntry
                title={exp.jobTitle || exp.company}
                meta={[exp.jobTitle ? exp.company : "", exp.dateRange].filter(Boolean).join(" / ")}
            >
                <BulletList lines={exp.bullets} />
            </BoldEntry>
        );
    }
    if (sidebar && split) {
        return (
            <RuledEntry title={exp.jobTitle || exp.company} date={exp.dateRange} subtitle={exp.jobTitle ? exp.company : ""}>
                <BulletList lines={exp.bullets} />
            </RuledEntry>
        );
    }
    if (sidebar) {
        return (
            <TimelineEntry
                lead={exp.dateRange}
                leadSub={exp.location}
                heading={exp.company || exp.jobTitle}
                subheading={exp.company ? exp.jobTitle : ""}
            >
                <BulletList lines={exp.bullets} />
            </TimelineEntry>
        );
    }
    return (
        <div className="ats-cv-entry">
            <SplitEntryHeader
                leftTitle={exp.jobTitle}
                leftSubtitle={exp.company}
                rightTitle={exp.dateRange}
                rightSubtitle={exp.location}
            />
            <BulletList lines={exp.bullets} />
        </div>
    );
}

function EducationBlock({ edu, sidebar, split, bold }) {
    const useBullets = edu.bullets.length > 0;
    if (sidebar && bold) {
        return (
            <BoldEntry
                title={edu.degree || edu.institution}
                sub={[edu.degree ? edu.institution : "", edu.graduationYear].filter(Boolean).join(" / ")}
            >
                {useBullets ? (
                    <BulletList lines={edu.bullets} />
                ) : edu.details ? (
                    <p className="ats-cv-paragraph" style={{ textAlign: "left" }}>
                        {edu.details}
                    </p>
                ) : null}
            </BoldEntry>
        );
    }
    if (sidebar && split) {
        return (
            <RuledEntry title={edu.degree || edu.institution} date={edu.graduationYear} subtitle={edu.degree ? edu.institution : ""}>
                {useBullets ? (
                    <BulletList lines={edu.bullets} />
                ) : edu.details ? (
                    <p className="ats-cv-paragraph" style={{ textAlign: "left" }}>
                        {edu.details}
                    </p>
                ) : null}
            </RuledEntry>
        );
    }
    if (sidebar) {
        return (
            <TimelineEntry
                lead={edu.graduationYear}
                leadSub={edu.location}
                heading={edu.institution || edu.degree}
                subheading={edu.institution ? edu.degree : ""}
            >
                {useBullets ? (
                    <BulletList lines={edu.bullets} />
                ) : edu.details ? (
                    <p className="ats-cv-paragraph" style={{ textAlign: "left" }}>
                        {edu.details}
                    </p>
                ) : null}
            </TimelineEntry>
        );
    }
    return (
        <div className="ats-cv-entry">
            <SplitEntryHeader
                leftTitle={edu.degree}
                leftSubtitle={edu.institution}
                rightTitle={edu.graduationYear}
                rightSubtitle={edu.location}
            />
            {useBullets ? (
                <BulletList lines={edu.bullets} />
            ) : edu.details ? (
                <p className="ats-cv-paragraph" style={{ textAlign: "left" }}>
                    {edu.details}
                </p>
            ) : null}
        </div>
    );
}

function ProjectTitleLink({ link, linkHref }) {
    const href = linkHref || normalizeProjectUrl(link);
    if (!href) return null;
    const { label, isGithub } = getProjectLinkMeta(link);

    return (
        <a
            href={href}
            className="ats-cv-project-link"
            data-export-icon={isGithub ? "github" : "link"}
            title={href}
            aria-label={`${label}: ${href}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                openProjectLink(href);
            }}
        >
            {isGithub ? (
                <IconBrandGithub className="ats-cv-project-link__icon" size={11} stroke={1.8} aria-hidden />
            ) : (
                <span className="ats-cv-project-link__text">{label}</span>
            )}
        </a>
    );
}

function ProjectBlock({ proj, sidebar, split, bold }) {
    if (sidebar && bold) {
        return (
            <BoldEntry title={proj.name} meta={proj.date} sub={proj.technologies}>
                <BulletList lines={proj.bullets} />
            </BoldEntry>
        );
    }
    if (sidebar && split) {
        return (
            <RuledEntry title={proj.name} date={proj.date} subtitle={proj.technologies}>
                <BulletList lines={proj.bullets} />
            </RuledEntry>
        );
    }
    if (sidebar) {
        return (
            <TimelineEntry
                lead={proj.date}
                heading={(
                    <div className="ats-cv-split-row__title ats-cv-split-row__title--project">
                        <span>{proj.name}</span>
                        {proj.linkHref || proj.link ? (
                            <ProjectTitleLink link={proj.link} linkHref={proj.linkHref} />
                        ) : null}
                    </div>
                )}
                subheading={proj.technologies}
            >
                <BulletList lines={proj.bullets} />
            </TimelineEntry>
        );
    }
    return (
        <div className="ats-cv-entry">
            <div className="ats-cv-split-row">
                <div className="ats-cv-split-row__left">
                    <div className="ats-cv-split-row__title ats-cv-split-row__title--project">
                        <span>{proj.name}</span>
                        {proj.linkHref || proj.link ? (
                            <ProjectTitleLink link={proj.link} linkHref={proj.linkHref} />
                        ) : null}
                    </div>
                    {proj.technologies ? (
                        <p className="ats-cv-split-row__subtitle">{proj.technologies}</p>
                    ) : null}
                </div>
                <div className="ats-cv-split-row__right">
                    {proj.date ? <p className="ats-cv-split-row__title">{proj.date}</p> : null}
                </div>
            </div>
            <BulletList lines={proj.bullets} />
        </div>
    );
}

function CustomSectionContent({ section }) {
    if (section.layout === "paragraph" && section.paragraph) {
        return <p className="ats-cv-paragraph">{section.paragraph}</p>;
    }
    if (section.layout === "twoColumn") {
        return <TwoColumnBullets items={section.items} />;
    }
    return (
        <div className="ats-cv-list-block">
            <BulletList lines={section.items} />
        </div>
    );
}

function PreviewBlock({ blockId, d, t, sidebar = false, split = false, banner = false, rail = false, bold = false }) {
    if (blockId === "header") {
        if (banner) return <BannerHeader d={d} />;
        if (bold) return <BoldHeader d={d} />;
        return rail ? <SidebarMainHeader d={d} split={split} /> : <CvHeader d={d} />;
    }

    if (blockId === "summary-title") {
        return <SectionTitleBar title={t.summary} rule={sidebar} split={split} pill={banner} />;
    }
    if (blockId === "summary-body") {
        return <p className="ats-cv-summary ats-cv-section__body-standalone">{d.summary}</p>;
    }

    if (blockId === "experience-title") {
        return <SectionTitleBar title={t.experience} rule={sidebar} split={split} />;
    }
    const expMatch = blockId.match(/^experience-(\d+)$/);
    if (expMatch) {
        const exp = d.experiences[Number(expMatch[1])];
        return exp ? <ExperienceBlock exp={exp} sidebar={sidebar} split={split} bold={bold} /> : null;
    }

    if (blockId === "education-title") {
        return <SectionTitleBar title={t.education} rule={sidebar} split={split} />;
    }
    const eduMatch = blockId.match(/^education-(\d+)$/);
    if (eduMatch) {
        const edu = d.education[Number(eduMatch[1])];
        return edu ? <EducationBlock edu={edu} sidebar={sidebar} split={split} bold={bold} /> : null;
    }

    if (blockId === "projects-title") {
        return <SectionTitleBar title={t.projects} rule={sidebar} split={split} />;
    }
    const projMatch = blockId.match(/^project-(\d+)$/);
    if (projMatch) {
        const proj = d.projects[Number(projMatch[1])];
        return proj ? <ProjectBlock proj={proj} sidebar={sidebar} split={split} bold={bold} /> : null;
    }

    if (blockId === "skills-title") {
        return <SectionTitleBar title={t.skills} rule={sidebar} split={split} />;
    }
    if (blockId === "skills-body") {
        return (
            <div className="ats-cv-section__body-standalone">
                <TwoColumnBullets items={d.skills} />
            </div>
        );
    }

    if (blockId === "certifications-title") {
        return <SectionTitleBar title={t.certifications} rule={sidebar} split={split} />;
    }
    if (blockId === "certifications-body") {
        return (
            <div className="ats-cv-section__body-standalone">
                <TwoColumnBullets items={d.certifications} />
            </div>
        );
    }

    if (blockId === "languages-title") {
        return <SectionTitleBar title={t.languages} rule={sidebar} split={split} />;
    }
    if (blockId === "languages-body") {
        return (
            <div className="ats-cv-section__body-standalone">
                <TwoColumnBullets items={d.languages} />
            </div>
        );
    }

    const customTitleMatch = blockId.match(/^custom-(.+)-title$/);
    if (customTitleMatch) {
        const sec = d.customSections.find((s) => s.id === customTitleMatch[1]);
        return sec ? <SectionTitleBar title={sec.title} rule={sidebar} split={split} /> : null;
    }
    const customBodyMatch = blockId.match(/^custom-(.+)-body$/);
    if (customBodyMatch) {
        const sec = d.customSections.find((s) => s.id === customBodyMatch[1]);
        return sec ? (
            <div className="ats-cv-section__body-standalone">
                <CustomSectionContent section={sec} />
            </div>
        ) : null;
    }

    return null;
}

function findSectionTitleBlockId(block, blocks) {
    if (!block?.sectionTitle) return null;
    const titleBlock = blocks.find(
        (b) => b.kind === "section-title" && b.sectionTitle === block.sectionTitle
    );
    return titleBlock?.id ?? null;
}

/** Repeat section heading when a page starts mid-section or continues from a prior page. */
function needsSectionTitleOnPage(pageIds, index, blocks, block) {
    if (!block || (block.kind !== "entry" && block.kind !== "section-body")) return false;
    if (!block.sectionTitle) return false;

    const titleBlockId = findSectionTitleBlockId(block, blocks);
    if (titleBlockId && pageIds.includes(titleBlockId)) {
        return false;
    }

    const prevId = index > 0 ? pageIds[index - 1] : null;
    const prevBlock = prevId ? blocks.find((b) => b.id === prevId) : null;

    if (prevBlock?.sectionTitle === block.sectionTitle) {
        return false;
    }

    return true;
}

function AtsCvPreview({ form, activePreviewPage = null, template = 'classic' }, ref) {
    // The template class is stamped onto the measure layer AND every page sheet
    // (not just the root) because the PDF export clones each `.ats-cv-page-sheet`
    // out to document.body and the print export clones `.ats-cv-pages-stack` into
    // an iframe — an ancestor-only class would be lost in both. Keeping it on the
    // sheet keeps the theme intact through export, and on the measure layer keeps
    // pagination measuring the theme's real heights.
    const tplClass = ` tpl-${template || 'classic'}`;
    const split = template === 'split';
    const banner = template === 'banner';
    // `rail` is the two-column family (a left rail on every page); `sidebar` is what the
    // entries and headings take their dated, ruled look from, which the banner shares.
    const bold = template === 'bold';
    const rail = template === 'sidebar' || split || bold;
    const sidebar = rail || banner;
    const sheetStyle = rail ? RAIL_SHEET_STYLES[template] : banner ? BANNER_SHEET_STYLE : PAGE_SHEET_STYLE;
    const { blocks, data: d, titles: t } = useMemo(
        () => buildAtsCvPreviewBlockMeta(form, template),
        [form, template]
    );
    const measureRef = useRef(null);
    const [pageIds, setPageIds] = useState(() => {
        const ids = blocks.map((b) => b.id);
        return ids.length ? [ids] : [[]];
    });

    useLayoutEffect(() => {
        const root = measureRef.current;
        if (!root) return;

        const measure = () => {
            const heights = blocks.map((block) => {
                const el = root.querySelector(`[data-preview-block="${block.id}"]`);
                return measurePreviewBlockHeight(el);
            });
            const packed = packBlocksIntoPages(blocks, heights, ATS_CV_PAGE_CONTENT_HEIGHT);
            setPageIds(packed.length > 0 ? packed : [[]]);
        };

        measure();
        if (!blocks.length) return undefined;

        const ro = new ResizeObserver(measure);
        ro.observe(root);
        return () => ro.disconnect();
    }, [form, blocks, sheetStyle]);

    const displayPages = pageIds.length > 0 ? pageIds : [[]];
    const isEmptyPreview = blocks.length === 0;
    const isPagedPreview =
        activePreviewPage !== null &&
        activePreviewPage !== undefined &&
        displayPages.length > 1;
    const showPageLabel = displayPages.length > 1 && !isPagedPreview;

    return (
        <div ref={ref} className={`ats-cv-pages-root${tplClass}`} data-ats-cv-pages>
            {/* Hidden measure layer */}
            <div
                ref={measureRef}
                className={`ats-cv-measure-layer${tplClass}`}
                style={{
                    ...sheetStyle,
                    position: "fixed",
                    left: -12000,
                    top: 0,
                    visibility: "hidden",
                    pointerEvents: "none",
                    height: "auto",
                    overflow: "visible",
                }}
                aria-hidden
            >
                {blocks.map((block) => (
                    <div key={block.id} data-preview-block={block.id}>
                        <PreviewBlock blockId={block.id} d={d} t={t} sidebar={sidebar} split={split} banner={banner} rail={rail} bold={bold} />
                    </div>
                ))}
            </div>

            <div
                className={`ats-cv-pages-stack ats-cv-pages-stack--snap${isPagedPreview ? " ats-cv-pages-stack--paged-preview" : ""}`}
            >
                {displayPages.map((ids, pageIndex) => (
                    <div
                        key={`page-${pageIndex}`}
                        className={`ats-cv-page-slide${isPagedPreview && pageIndex === activePreviewPage ? " is-active" : ""}`}
                    >
                        <div
                            className={`ats-cv-page-sheet${tplClass}${isEmptyPreview ? " ats-cv-page-sheet--blank" : ""}`}
                            data-ats-cv-page
                            data-page-index={pageIndex}
                            style={sheetStyle}
                            aria-label={isEmptyPreview ? "Blank CV page" : undefined}
                        >
                            {rail ? <SidebarBackground /> : null}
                            {rail && pageIndex === 0 ? <SidebarContent d={d} t={t} split={split} bold={bold} /> : null}
                            {showPageLabel && (
                                <span className="ats-cv-page-sheet__badge">
                                    পৃষ্ঠা {pageIndex + 1} / {displayPages.length}
                                </span>
                            )}
                            {ids.map((blockId, idx) => {
                                const block = blocks.find((b) => b.id === blockId);
                                const showTitle =
                                    (block?.kind === "entry" || block?.kind === "section-body") &&
                                    needsSectionTitleOnPage(ids, idx, blocks, block);

                                return (
                                    <div key={blockId}>
                                        {showTitle && block.sectionTitle ? (
                                            <SectionTitleBar title={block.sectionTitle} rule={sidebar} split={split} />
                                        ) : null}
                                        <PreviewBlock blockId={blockId} d={d} t={t} sidebar={sidebar} split={split} banner={banner} rail={rail} bold={bold} />
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default forwardRef(AtsCvPreview);
